/**
 * src/realtime/liveSync.ts — W3 live data propagation (web-companion spec §2.4).
 *
 * Subscribes to the backend SSE channel `GET /api/events` and, on every org-scoped invalidation
 * hint, invalidates the React-Query cache so a change made on one device shows up here within ~1s.
 *
 * PLATFORM: uses the browser `EventSource` global. On web (react-native-web) it exists; on native
 * it does not, so `startLiveSync` no-ops and native keeps relying on refetch-on-focus + a short
 * staleTime (spec §2.4 baseline). Native can adopt a header-capable EventSource polyfill later
 * without touching this contract.
 *
 * AUTH: the browser EventSource cannot set headers, so we mint a short-lived (~60s) single-purpose
 * SSE TICKET via POST /api/auth/sse-ticket and pass it as `?ticket=` — the full access token is
 * NEVER put in the URL (it would leak into access/proxy logs). On any stream error (including
 * ticket expiry) we close, refresh the access token, mint a fresh ticket, and reconnect with capped
 * exponential backoff. EventSource's own auto-reconnect is disabled (it would retry the stale URL).
 *
 * INVALIDATION: hints carry only `{entityType, entityId, action}` — no payload. Rather than maintain
 * a brittle entityType→queryKey map, we coalesce bursts and invalidate all *active* queries (only
 * mounted queries refetch, so the cost is the 1–2 lists currently on screen). The changed data's
 * query is therefore always refreshed. A targeted-key map is a possible future optimization.
 *
 * NEVER imports @smartbox/* (mobile invariant 1). No PII is logged.
 */
import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL } from '../config';
import { sseTicket } from '../api/client';
import { useSession } from '../auth/session';

const COALESCE_MS = 750;
const MAX_BACKOFF_MS = 30_000;
const INITIAL_BACKOFF_MS = 1_000;

type Stop = () => void;

/** True when a usable EventSource implementation is present (web). */
function hasEventSource(): boolean {
  return typeof (globalThis as { EventSource?: unknown }).EventSource !== 'undefined';
}

/**
 * Open the SSE connection and wire invalidation. Returns a stop() that closes the stream and
 * cancels timers. Safe to call on native (returns a no-op stop).
 */
export function startLiveSync(
  queryClient: QueryClient,
  getToken: () => string | null,
  refreshToken: () => Promise<string | null>,
): Stop {
  if (!hasEventSource()) return () => {};

  const ES = (globalThis as unknown as { EventSource: typeof EventSource }).EventSource;
  let es: EventSource | null = null;
  let stopped = false;
  let backoff = INITIAL_BACKOFF_MS;
  let coalesceTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  function scheduleInvalidate(): void {
    if (coalesceTimer) return;
    coalesceTimer = setTimeout(() => {
      coalesceTimer = null;
      // Invalidate active queries → the on-screen lists/details refetch.
      void queryClient.invalidateQueries();
    }, COALESCE_MS);
  }

  function cleanupConnection(): void {
    if (es) {
      try { es.close(); } catch { /* noop */ }
      es = null;
    }
  }

  async function connect(): Promise<void> {
    if (stopped) return;
    const token = getToken();
    if (!token) {
      // Not authenticated yet — try again shortly (cheap; stops on unmount).
      reconnectTimer = setTimeout(() => void connect(), 2_000);
      return;
    }
    // Mint a short-lived SSE ticket (Bearer-authed) — never put the access token in the URL.
    const ticket = await sseTicket();
    if (stopped) return;
    if (!ticket) {
      // Couldn't mint (access token likely lapsed) — refresh + retry shortly.
      reconnectTimer = setTimeout(() => void refreshToken().catch(() => null).then(() => connect()), 2_000);
      return;
    }
    const url = `${API_BASE_URL}/api/events?ticket=${encodeURIComponent(ticket)}`;
    es = new ES(url);

    es.addEventListener('domain', () => scheduleInvalidate());

    es.onopen = () => {
      backoff = INITIAL_BACKOFF_MS; // reset backoff once connected
    };

    es.onerror = () => {
      // Disable EventSource's built-in retry (stale-token URL); reconnect ourselves with a fresh token.
      cleanupConnection();
      if (stopped) return;
      const delay = backoff;
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      reconnectTimer = setTimeout(() => {
        // The token may have lapsed → refresh before reconnecting (best-effort).
        void refreshToken().catch(() => null).then(() => connect());
      }, delay);
    };
  }

  void connect();

  return () => {
    stopped = true;
    if (coalesceTimer) { clearTimeout(coalesceTimer); coalesceTimer = null; }
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    cleanupConnection();
  };
}

/**
 * Hook: start live-sync while authenticated, stop on sign-out / unmount. Call once high in the
 * authenticated tree (the (app) layout). No-op on native and when signed out.
 */
export function useLiveSync(): void {
  const queryClient = useQueryClient();
  const status = useSession((s) => s.status);

  useEffect(() => {
    if (status !== 'authenticated') return;
    if (!hasEventSource()) return;
    const stop = startLiveSync(
      queryClient,
      () => useSession.getState().token,
      () => useSession.getState().refresh(),
    );
    return stop;
  }, [status, queryClient]);
}
